import { PDFDocument, PDFFont, PDFPage, StandardFonts, rgb } from 'pdf-lib';
import { formatHours, OfficeReport, reportTitle } from '@/utils/officeReport';

// Paleta da marca
const GOLD = rgb(0.769, 0.671, 0.514); // #c4ab83
const GOLD_DARK = rgb(0.604, 0.498, 0.322); // #9a7f52
const CREAM = rgb(0.98, 0.969, 0.949); // #faf7f2
const INK = rgb(0.059, 0.09, 0.165); // #0f172a
const TEXT = rgb(0.2, 0.255, 0.333); // #334155
const MUTED = rgb(0.58, 0.639, 0.722); // #94a3b8
const LINE = rgb(0.886, 0.91, 0.941); // #e2e8f0
const SOFT = rgb(0.973, 0.98, 0.988); // #f8fafc
const GREEN = rgb(0.063, 0.725, 0.506); // #10b981
const RED = rgb(0.937, 0.267, 0.267); // #ef4444
const STATUS_COLORS = [GOLD, INK, GREEN, rgb(0.961, 0.62, 0.043), MUTED, RED];

const PAGE_W = 595.28; // A4
const PAGE_H = 841.89;
const MARGIN = 48;
const CONTENT_W = PAGE_W - MARGIN * 2;

export interface ReportPdfData {
  periodFrom?: Date;
  periodTo?: Date;
  filters: string[];
  financial: { revenue: number; expenses: number; profit: number };
  casesByStatus: { name: string; value: number }[];
  proBono: { totalCases: number; totalValue: number; activeMonths: number };
  cases: { title: string; client: string; type: string; status: string; createdAt: string }[];
  lawyers: { lawyer: string; casesCount: number; averageResolutionTime: string }[];
  office?: OfficeReport | null;
}

const brl = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0).replace(/ /g, ' ');
const dateBR = (d?: Date) => (d ? d.toLocaleDateString('pt-BR') : '');

export async function generateReportPdf(data: ReportPdfData): Promise<Uint8Array> {
  const pdf = await PDFDocument.create();
  const heading = reportTitle(data.periodFrom, data.periodTo);
  pdf.setTitle(`${heading.title} - Allana Sthéfany`);
  pdf.setAuthor('Allana Sthéfany Advocacia & Consultoria Jurídica');
  const regular = await pdf.embedFont(StandardFonts.Helvetica);
  const bold = await pdf.embedFont(StandardFonts.HelveticaBold);

  let logo: Awaited<ReturnType<PDFDocument['embedPng']>> | null = null;
  try {
    const bytes = await fetch(encodeURI('/imgs/logos allana/letra_A_logo.png')).then((r) => r.arrayBuffer());
    logo = await pdf.embedPng(bytes);
  } catch {
    logo = null;
  }

  // Remove caracteres que a fonte padrão do PDF não suporta (emojis, setas...)
  const safe = (font: PDFFont, text: string) =>
    String(text ?? '')
      .replace(/[−‒]/g, '-')
      .replace(/[→]/g, '->')
      .split('')
      .filter((ch) => {
        try {
          font.encodeText(ch);
          return true;
        } catch {
          return false;
        }
      })
      .join('');

  const fit = (font: PDFFont, text: string, size: number, maxWidth: number) => {
    let t = safe(font, text);
    if (font.widthOfTextAtSize(t, size) <= maxWidth) return t;
    while (t.length > 1 && font.widthOfTextAtSize(`${t}...`, size) > maxWidth) t = t.slice(0, -1);
    return `${t.trimEnd()}...`;
  };

  const pages: PDFPage[] = [];
  let page!: PDFPage;
  let y = 0;

  const text = (str: string, x: number, yy: number, size: number, font: PDFFont = regular, color = TEXT) =>
    page.drawText(safe(font, str), { x, y: yy, size, font, color });
  const textRight = (str: string, xRight: number, yy: number, size: number, font: PDFFont = regular, color = TEXT) => {
    const s = safe(font, str);
    page.drawText(s, { x: xRight - font.widthOfTextAtSize(s, size), y: yy, size, font, color });
  };

  const newPage = (withHeader: boolean) => {
    page = pdf.addPage([PAGE_W, PAGE_H]);
    pages.push(page);
    if (withHeader) {
      // Cabeçalho completo (primeira página)
      page.drawRectangle({ x: 0, y: PAGE_H - 150, width: PAGE_W, height: 150, color: CREAM });
      page.drawRectangle({ x: 0, y: PAGE_H - 152, width: PAGE_W, height: 2, color: GOLD });
      if (logo) {
        const h = 58;
        const w = (logo.width / logo.height) * h;
        page.drawImage(logo, { x: MARGIN, y: PAGE_H - 108, width: w, height: h });
        // Marca d'água discreta
        const wh = 190;
        page.drawImage(logo, { x: PAGE_W - 170, y: PAGE_H - 175, width: (logo.width / logo.height) * wh, height: wh, opacity: 0.07 });
      }
      const tx = MARGIN + 74;
      text('ALLANA STHÉFANY', tx, PAGE_H - 62, 9, bold, GOLD_DARK);
      text('Advocacia & Consultoria Jurídica', tx, PAGE_H - 75, 8.5, regular, MUTED);
      text(heading.title, tx, PAGE_H - 103, 24, bold, INK);
      if (heading.subtitle) {
        const sub = heading.subtitle.charAt(0).toUpperCase() + heading.subtitle.slice(1);
        text(sub, tx + bold.widthOfTextAtSize(heading.title, 24) + 10, PAGE_H - 103, 14, regular, GOLD_DARK);
      }
      const period = data.periodFrom
        ? `Período: ${dateBR(data.periodFrom)} a ${dateBR(data.periodTo || data.periodFrom)}`
        : 'Período: todo o histórico';
      text(period, tx, PAGE_H - 122, 9.5, regular, TEXT);
      textRight(`Emitido em ${new Date().toLocaleDateString('pt-BR')}`, PAGE_W - MARGIN, PAGE_H - 62, 8.5, regular, MUTED);
      y = PAGE_H - 182;
      if (data.filters.length) {
        text(`Filtros: ${data.filters.join('  ·  ')}`, MARGIN, y, 8.5, regular, MUTED);
        y -= 22;
      }
    } else {
      // Cabeçalho compacto (demais páginas)
      text('ALLANA STHÉFANY', MARGIN, PAGE_H - 40, 8, bold, GOLD_DARK);
      textRight(heading.subtitle ? `${heading.title} · ${heading.subtitle}` : heading.title, PAGE_W - MARGIN, PAGE_H - 40, 8, regular, MUTED);
      page.drawLine({ start: { x: MARGIN, y: PAGE_H - 48 }, end: { x: PAGE_W - MARGIN, y: PAGE_H - 48 }, thickness: 0.6, color: GOLD });
      y = PAGE_H - 74;
    }
  };

  const ensure = (height: number) => {
    if (y - height < 70) newPage(false);
  };

  const sectionTitle = (eyebrow: string, title: string) => {
    ensure(60);
    text(eyebrow.toUpperCase(), MARGIN, y, 7.5, bold, GOLD_DARK);
    y -= 17;
    text(title, MARGIN, y, 15, bold, INK);
    y -= 16;
  };

  newPage(true);

  // ===== Visão geral do escritório =====
  const office = data.office;
  if (office) {
    sectionTitle('Visão geral', 'O escritório no período');
    const ow = (CONTENT_W - 20) / 3;
    const oTiles = [
      { label: 'PROCESSOS NOVOS', value: String(office.totals.newProcesses), hint: `${office.totals.newJudicial} judiciais · ${office.totals.newAdministrative} administrativos` },
      { label: 'CLIENTES NOVOS', value: String(office.totals.newClients), hint: 'cadastrados no período' },
      { label: 'ATENDIMENTOS', value: String(office.totals.appointments), hint: `${office.totals.appointmentsDone} concluído${office.totals.appointmentsDone === 1 ? '' : 's'}${office.totals.appointmentsCanceled ? ` · ${office.totals.appointmentsCanceled} cancelado${office.totals.appointmentsCanceled === 1 ? '' : 's'}` : ''}` },
      { label: 'TEMPO TRABALHADO', value: formatHours(office.totals.workedSeconds), hint: 'registrado nas tarefas' },
      { label: 'TAREFAS CONCLUÍDAS', value: String(office.totals.tasksCompleted), hint: `${office.totals.tasksCreated} criadas no período` },
      { label: 'RESULTADO', value: brl(data.financial.profit), hint: 'receitas menos despesas' },
    ];
    oTiles.forEach((tile, i) => {
      const x = MARGIN + (i % 3) * (ow + 10);
      const top = y - Math.floor(i / 3) * 72;
      const empty = tile.value === '0' || tile.value === '0h';
      page.drawRectangle({ x, y: top - 62, width: ow, height: 62, color: SOFT, borderColor: LINE, borderWidth: 0.8 });
      page.drawRectangle({ x, y: top - 62, width: 3, height: 62, color: GOLD });
      text(tile.label, x + 14, top - 17, 7, bold, MUTED);
      text(fit(bold, tile.value, 16, ow - 24), x + 14, top - 38, 16, bold, empty ? MUTED : INK);
      text(fit(regular, tile.hint, 7.5, ow - 24), x + 14, top - 52, 7.5, regular, MUTED);
    });
    y -= 72 * 2 + 18;
  }

  // ===== Resumo financeiro =====
  sectionTitle('Resumo financeiro', 'Receitas, despesas e resultado');
  const tileW = (CONTENT_W - 20) / 3;
  const tiles = [
    { label: 'RECEITAS', value: data.financial.revenue, color: GREEN },
    { label: 'DESPESAS', value: data.financial.expenses, color: RED },
    { label: 'RESULTADO', value: data.financial.profit, color: data.financial.profit >= 0 ? INK : RED },
  ];
  tiles.forEach((tile, i) => {
    const x = MARGIN + i * (tileW + 10);
    page.drawRectangle({ x, y: y - 62, width: tileW, height: 62, color: SOFT, borderColor: LINE, borderWidth: 0.8 });
    page.drawRectangle({ x, y: y - 62, width: 3, height: 62, color: tile.color });
    text(tile.label, x + 14, y - 20, 7.5, bold, MUTED);
    text(fit(bold, brl(tile.value), 17, tileW - 24), x + 14, y - 44, 17, bold, tile.color);
  });
  y -= 82;
  const flow = data.financial.revenue + data.financial.expenses;
  const share = flow > 0 ? data.financial.revenue / flow : 0.5;
  page.drawRectangle({ x: MARGIN, y: y - 6, width: CONTENT_W, height: 6, color: LINE });
  page.drawRectangle({ x: MARGIN, y: y - 6, width: CONTENT_W * share, height: 6, color: GREEN });
  page.drawRectangle({ x: MARGIN + CONTENT_W * share, y: y - 6, width: CONTENT_W * (1 - share), height: 6, color: RED, opacity: 0.75 });
  y -= 20;
  text('Proporção entre receitas e despesas no período. Custas pagas pelo cliente não entram nos totais.', MARGIN, y, 8, regular, MUTED);
  y -= 34;

  if (office) {
    // ===== Mês a mês =====
    sectionTitle('Evolução', 'Mês a mês');
    const mCols = [
      { label: 'MÊS', width: 52, right: false },
      { label: 'PROC.', width: 40, right: true },
      { label: 'CLIENTES', width: 48, right: true },
      { label: 'ATEND.', width: 44, right: true },
      { label: 'TEMPO', width: 58, right: true },
      { label: 'TAREFAS', width: 46, right: true },
      { label: 'RECEITAS', width: 70, right: true },
      { label: 'DESPESAS', width: 70, right: true },
      { label: 'RESULTADO', width: CONTENT_W - 52 - 40 - 48 - 44 - 58 - 46 - 70 - 70, right: true },
    ];
    const headColor = rgb(0.91, 0.835, 0.659);
    const drawMonthHeader = () => {
      page.drawRectangle({ x: MARGIN, y: y - 20, width: CONTENT_W, height: 20, color: INK });
      let x = MARGIN;
      mCols.forEach((c) => {
        if (c.right) textRight(c.label, x + c.width - 6, y - 13, 6.5, bold, headColor);
        else text(c.label, x + 8, y - 13, 6.5, bold, headColor);
        x += c.width;
      });
      y -= 20;
    };
    ensure(60);
    drawMonthHeader();
    type Cell = { v: string; color: ReturnType<typeof rgb> };
    const drawMonthRow = (values: Cell[], shaded: boolean, strong: boolean) => {
      if (y - 19 < 70) {
        newPage(false);
        drawMonthHeader();
      }
      if (shaded) page.drawRectangle({ x: MARGIN, y: y - 19, width: CONTENT_W, height: 19, color: strong ? CREAM : SOFT });
      let x = MARGIN;
      values.forEach((cell, i) => {
        const c = mCols[i];
        const font = strong || i === 0 ? bold : regular;
        const str = fit(font, cell.v, 7.8, c.width - 8);
        if (c.right) textRight(str, x + c.width - 6, y - 12.5, 7.8, font, cell.color);
        else text(str, x + 8, y - 12.5, 7.8, font, cell.color);
        x += c.width;
      });
      page.drawLine({ start: { x: MARGIN, y: y - 19 }, end: { x: PAGE_W - MARGIN, y: y - 19 }, thickness: 0.4, color: LINE });
      y -= 19;
    };
    const sum = { processes: 0, clients: 0, appointments: 0, workedSeconds: 0, tasksCompleted: 0, revenue: 0, expenses: 0 };
    const num = (v: number): Cell => ({ v: String(v), color: v ? INK : MUTED });
    const money = (v: number, color: ReturnType<typeof rgb>): Cell => ({ v: brl(v), color: v ? color : MUTED });
    const cellsFor = (m: typeof sum, label: string): Cell[] => {
      const result = m.revenue - m.expenses;
      return [
        { v: label, color: INK },
        num(m.processes), num(m.clients), num(m.appointments),
        { v: formatHours(m.workedSeconds), color: m.workedSeconds ? INK : MUTED },
        num(m.tasksCompleted),
        money(m.revenue, GREEN), money(m.expenses, RED),
        money(result, result >= 0 ? GREEN : RED),
      ];
    };
    office.months.forEach((m, i) => {
      (Object.keys(sum) as (keyof typeof sum)[]).forEach((k) => (sum[k] += m[k]));
      drawMonthRow(cellsFor(m, m.label), i % 2 === 1, false);
    });
    if (office.months.length > 1) drawMonthRow(cellsFor(sum, 'Total'), true, true);
    y -= 30;

    // ===== Atendimentos por tipo + onde o tempo foi =====
    const colW = (CONTENT_W - 24) / 2;
    const x2 = MARGIN + colW + 24;
    const rows = Math.max(office.appointmentsByType.length, office.topTasks.length, 1);
    ensure(50 + rows * 20);
    text('AGENDA', MARGIN, y, 7.5, bold, GOLD_DARK);
    text('PRODUTIVIDADE', x2, y, 7.5, bold, GOLD_DARK);
    y -= 17;
    text('Atendimentos por tipo', MARGIN, y, 13, bold, INK);
    text('Onde o tempo foi', x2, y, 13, bold, INK);
    y -= 12;
    const listTop = y;
    if (office.appointmentsByType.length === 0) {
      text('Nenhum atendimento no período.', MARGIN, listTop - 12, 9, regular, MUTED);
    } else {
      const max = office.appointmentsByType[0].value;
      office.appointmentsByType.forEach((t, i) => {
        const yy = listTop - i * 20;
        text(fit(regular, t.name, 9, colW - 40), MARGIN, yy - 11, 9, regular, TEXT);
        textRight(String(t.value), MARGIN + colW, yy - 11, 9, bold, INK);
        page.drawRectangle({ x: MARGIN, y: yy - 17, width: colW, height: 2.5, color: LINE });
        page.drawRectangle({ x: MARGIN, y: yy - 17, width: Math.max(3, (t.value / max) * colW), height: 2.5, color: GOLD });
      });
    }
    if (office.topTasks.length === 0) {
      text('Nenhum tempo registrado no período.', x2, listTop - 12, 9, regular, MUTED);
    } else {
      office.topTasks.forEach((t, i) => {
        const yy = listTop - i * 20;
        text(fit(regular, t.title, 9, colW - 70), x2, yy - 11, 9, regular, TEXT);
        textRight(formatHours(t.seconds), x2 + colW, yy - 11, 9, bold, INK);
        page.drawLine({ start: { x: x2, y: yy - 17 }, end: { x: x2 + colW, y: yy - 17 }, thickness: 0.4, color: LINE });
      });
    }
    y = listTop - rows * 20 - 30;

    // ===== Clientes novos =====
    if (office.newClientsList.length > 0) {
      sectionTitle('Carteira', `Clientes novos (${office.newClientsList.length})`);
      office.newClientsList.forEach((c) => {
        ensure(20);
        text(fit(regular, c.name, 9.5, CONTENT_W - 90), MARGIN, y - 11, 9.5, regular, INK);
        textRight(c.date, PAGE_W - MARGIN, y - 11, 9, regular, MUTED);
        page.drawLine({ start: { x: MARGIN, y: y - 18 }, end: { x: PAGE_W - MARGIN, y: y - 18 }, thickness: 0.4, color: LINE });
        y -= 20;
      });
      y -= 26;
    }
  }

  // ===== Processos por status =====
  sectionTitle('Processos', 'Distribuição por status');
  const totalStatus = data.casesByStatus.reduce((s, c) => s + c.value, 0);
  if (totalStatus === 0) {
    text('Nenhum processo no período.', MARGIN, y - 4, 9.5, regular, MUTED);
    y -= 30;
  } else {
    const maxValue = Math.max(...data.casesByStatus.map((c) => c.value));
    data.casesByStatus.forEach((item, i) => {
      ensure(22);
      const color = STATUS_COLORS[i % STATUS_COLORS.length];
      text(fit(regular, item.name, 9.5, 150), MARGIN, y - 9, 9.5, regular, TEXT);
      const barX = MARGIN + 160;
      const barW = CONTENT_W - 160 - 70;
      page.drawRectangle({ x: barX, y: y - 11, width: barW, height: 9, color: SOFT });
      page.drawRectangle({ x: barX, y: y - 11, width: Math.max(4, (item.value / maxValue) * barW), height: 9, color });
      const pct = Math.round((item.value / totalStatus) * 100);
      textRight(`${item.value}  (${pct}%)`, PAGE_W - MARGIN, y - 9, 9.5, bold, INK);
      y -= 21;
    });
    y -= 20;
  }

  // ===== Pro bono =====
  sectionTitle('Impacto social', 'Pro bono');
  const pbTiles = [
    { label: 'CASOS', value: String(data.proBono.totalCases) },
    { label: 'VALOR SOCIAL', value: brl(data.proBono.totalValue) },
    { label: 'MESES ATIVOS', value: String(data.proBono.activeMonths) },
  ];
  pbTiles.forEach((tile, i) => {
    const x = MARGIN + i * (tileW + 10);
    page.drawRectangle({ x, y: y - 48, width: tileW, height: 48, color: SOFT, borderColor: LINE, borderWidth: 0.8 });
    text(tile.label, x + 14, y - 18, 7.5, bold, MUTED);
    text(fit(bold, tile.value, 13, tileW - 24), x + 14, y - 36, 13, bold, INK);
  });
  y -= 76;

  // ===== Processos do período =====
  sectionTitle('Detalhamento', `Processos do período (${data.cases.length})`);
  const cols = [
    { label: 'PROCESSO', width: 182 },
    { label: 'CLIENTE', width: 110 },
    { label: 'TIPO', width: 82 },
    { label: 'STATUS', width: 70 },
    { label: 'CADASTRO', width: CONTENT_W - 182 - 110 - 82 - 70 },
  ];
  const drawTableHeader = () => {
    page.drawRectangle({ x: MARGIN, y: y - 20, width: CONTENT_W, height: 20, color: INK });
    let x = MARGIN + 8;
    cols.forEach((c) => {
      text(c.label, x, y - 13, 7, bold, rgb(0.91, 0.835, 0.659));
      x += c.width;
    });
    y -= 20;
  };
  if (data.cases.length === 0) {
    text('Nenhum processo encontrado com os filtros escolhidos.', MARGIN, y - 4, 9.5, regular, MUTED);
    y -= 30;
  } else {
    drawTableHeader();
    data.cases.forEach((row, index) => {
      if (y - 20 < 70) {
        newPage(false);
        drawTableHeader();
      }
      if (index % 2 === 1) page.drawRectangle({ x: MARGIN, y: y - 20, width: CONTENT_W, height: 20, color: SOFT });
      const values = [row.title, row.client, row.type, row.status, row.createdAt];
      let x = MARGIN + 8;
      values.forEach((v, i) => {
        const font = i === 0 ? bold : regular;
        text(fit(font, v || '-', 8, cols[i].width - 10), x, y - 13, 8, font, i === 0 ? INK : TEXT);
        x += cols[i].width;
      });
      page.drawLine({ start: { x: MARGIN, y: y - 20 }, end: { x: PAGE_W - MARGIN, y: y - 20 }, thickness: 0.4, color: LINE });
      y -= 20;
    });
    y -= 26;
  }

  // ===== Responsáveis =====
  if (data.lawyers.length > 0) {
    sectionTitle('Equipe', 'Desempenho por responsável');
    data.lawyers.forEach((l) => {
      ensure(24);
      text(fit(bold, l.lawyer, 10, 260), MARGIN, y - 12, 10, bold, INK);
      const avgDays = Math.round(parseFloat(l.averageResolutionTime));
      textRight(`${l.casesCount} ${l.casesCount === 1 ? 'processo' : 'processos'}`, PAGE_W - MARGIN - 160, y - 12, 9.5, regular, TEXT);
      textRight(Number.isFinite(avgDays) ? `tempo médio de ${avgDays} ${avgDays === 1 ? 'dia' : 'dias'}` : 'tempo médio: -', PAGE_W - MARGIN, y - 12, 9.5, regular, MUTED);
      page.drawLine({ start: { x: MARGIN, y: y - 20 }, end: { x: PAGE_W - MARGIN, y: y - 20 }, thickness: 0.4, color: LINE });
      y -= 24;
    });
  }

  // ===== Rodapé em todas as páginas =====
  pages.forEach((p, i) => {
    page = p;
    p.drawLine({ start: { x: MARGIN, y: 44 }, end: { x: PAGE_W - MARGIN, y: 44 }, thickness: 0.6, color: GOLD });
    text('Allana Sthéfany  ·  Advocacia & Consultoria Jurídica  ·  Documento de uso interno', MARGIN, 30, 7.5, regular, MUTED);
    textRight(`Página ${i + 1} de ${pages.length}`, PAGE_W - MARGIN, 30, 7.5, bold, GOLD_DARK);
  });

  return pdf.save();
}

export function downloadPdf(bytes: Uint8Array, filename: string) {
  const blob = new Blob([bytes], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
